import { Box, Typography } from "@material-ui/core";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import * as dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import useStyles from "./DeviceStats.styles";
import { ToolTip } from "../ToolTip/ToolTip";
import { HorizontalBarChart } from "../HorizontalBarChart/HorizontalBarChart";
import { DeviceStatCardGroup } from "../DeviceStatCardGroup/DeviceStatCardGroup";
import { dateFormat } from "../../_constants";

const TooltipContent = ({ analyzer }) => {
  const { t } = useTranslation(["analyserUpdates"]);
  const classes = useStyles();
  const items = t("analyserUpdates:analyzer.adoption.tooltip.items", {
    returnObjects: true,
    analyzer: analyzer,
  });

  return (
    <div className={classes.toolTipText}>
      <div style={{ marginBottom: 8 }}>
        {t("analyserUpdates:analyzer.adoption.tooltip.header")}
      </div>
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {Object.values(items).map((line, index) => {
          const [label, ...rest] = line.split(":");
          return (
            <li key={index}>
              <strong>{label}:</strong>
              {rest.join(":")}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const DeviceStats = ({
  activeTargets = 0,
  activeTargetsReadyToInstall = 0,
  activeTargetsOtherVersion = 0,
  createdDateInMillis,
  analyzer,
}) => {
  const classes = useStyles();
  const { t } = useTranslation(["analyserUpdates"]);

  const totalDevices =
    activeTargets + activeTargetsReadyToInstall + activeTargetsOtherVersion;

  const getPercentage = (count) =>
    totalDevices ? (count / totalDevices) * 100 : 0;

  const chartData = [
    getPercentage(activeTargets),
    getPercentage(activeTargetsReadyToInstall),
    getPercentage(activeTargetsOtherVersion),
  ];
  dayjs.extend(utc);

  const formattedDate = createdDateInMillis
    ? dayjs.utc(createdDateInMillis).format(dateFormat)
    : null;

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box className={classes.dateWrapper}>
          <Typography variant="body2" className={classes.dateText}>
            {formattedDate
              ? t("fuseUpdates:Updates.adoption.asOf", {
                  dateValue: formattedDate,
                })
              : t("fuseUpdates:Updates.adoption.noData")}
          </Typography>
          <span className={classes.infoIcon}>
            <ToolTip
              label={<TooltipContent analyzer={analyzer} />}
              variant="white"
              placement="bottom-start"
            />
          </span>
        </Box>

        <Box className={classes.barContainer}>
          <HorizontalBarChart values={chartData} />
        </Box>

        <Box className={classes.cardContainer}>
          <DeviceStatCardGroup
            stats={[
              {
                label: t("analyserUpdates:analyzer.adoption.devicesVersion", {
                  analyzer: analyzer,
                }),
                value: activeTargets,
              },
              {
                label: t("analyserUpdates:analyzer.adoption.otherDevices", {
                  analyzer: analyzer,
                }),
                value: activeTargetsOtherVersion,
              },
            ]}
          />
        </Box>
      </Box>
    </Box>
  );
};

DeviceStats.propTypes = {
  activeTargets: PropTypes.number,
  activeTargetsReadyToInstall: PropTypes.number,
  activeTargetsOtherVersion: PropTypes.number,
  createdDateInMillis: PropTypes.any,
  analyzer: PropTypes.string,
};

TooltipContent.propTypes = {
  analyzer: PropTypes.string.isRequired,
};

export { DeviceStats };
